import { z } from 'zod';
export const EntryCategory = z.enum([
    'med',
    'symptom',
    'note',
    'handover',
    'meal',
    'appointment',
]);
export const EntryCategoryLabels = {
    med: 'Medication',
    symptom: 'Symptom',
    note: 'Note',
    handover: 'Handover',
    meal: 'Meal',
    appointment: 'Appointment',
};
export const Score = z.number().int().min(0).max(10);
export const EntrySource = z.enum(['web', 'telegram']);
export const SymptomReadingSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    authorId: z.string(),
    authorName: z.string(),
    breathlessness: Score.nullable(),
    pain: Score.nullable(),
    sleep: Score.nullable(),
    appetite: Score.nullable(),
    mood: Score.nullable(),
    appearance: Score.nullable(),
    notes: z.string().nullable(),
    source: EntrySource,
    createdAt: z.string(),
});
export const SymptomReadingInput = z.object({
    occurredAt: z.string().datetime().optional(),
    breathlessness: Score.nullable().optional(),
    pain: Score.nullable().optional(),
    sleep: Score.nullable().optional(),
    appetite: Score.nullable().optional(),
    mood: Score.nullable().optional(),
    appearance: Score.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const EntrySchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    authorId: z.string(),
    authorName: z.string(),
    category: EntryCategory,
    title: z.string().nullable(),
    body: z.string(),
    medName: z.string().nullable(),
    medDose: z.string().nullable(),
    source: EntrySource,
    createdAt: z.string(),
});
export const EntryInput = z.object({
    occurredAt: z.string().datetime().optional(),
    category: EntryCategory,
    title: z.string().max(200).nullable().optional(),
    body: z.string().min(1).max(4000),
    medName: z.string().max(120).nullable().optional(),
    medDose: z.string().max(120).nullable().optional(),
});
export const UserSchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    role: z.enum(['family', 'nurse', 'admin']),
    telegramLinked: z.boolean(),
    createdAt: z.string(),
});
// Admin view — same User plus `disabled` and `lastSeenAt`.
export const AdminUserSchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    role: z.enum(['family', 'nurse', 'admin']),
    disabled: z.boolean(),
    telegramLinked: z.boolean(),
    telegramUsername: z.string().nullable(),
    createdAt: z.string(),
    lastSeenAt: z.string().nullable(),
});
export const AdminUserCreate = z.object({
    email: z.email(),
    name: z.string().min(1).max(100),
    role: z.enum(['family', 'nurse', 'admin']).default('family'),
});
export const AdminUserUpdate = z.object({
    name: z.string().min(1).max(100).optional(),
    role: z.enum(['family', 'nurse', 'admin']).optional(),
    disabled: z.boolean().optional(),
});
export const MagicLinkRequest = z.object({
    email: z.email(),
});
export const MagicLinkVerify = z.object({
    token: z.string().min(10),
});
export const HandoverSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    authorId: z.string(),
    authorName: z.string(),
    incomingName: z.string().nullable(),
    lastMedAt: z.string().nullable(),
    nextMedAt: z.string().nullable(),
    moodNote: z.string().nullable(),
    bodyNote: z.string(),
    createdAt: z.string(),
});
export const HandoverInput = z.object({
    occurredAt: z.string().datetime().optional(),
    incomingName: z.string().max(120).nullable().optional(),
    lastMedAt: z.string().datetime().nullable().optional(),
    nextMedAt: z.string().datetime().nullable().optional(),
    moodNote: z.string().max(500).nullable().optional(),
    bodyNote: z.string().min(1).max(4000),
});
// Shifts / calendar roster
export const ShiftKind = z.enum(['family', 'nurse', 'other']);
export const ShiftSchema = z.object({
    id: z.string(),
    startAt: z.string(),
    endAt: z.string(),
    kind: ShiftKind,
    assigneeId: z.string().nullable(),
    assigneeName: z.string().nullable(),
    label: z.string(),
    notes: z.string().nullable(),
    createdById: z.string(),
    createdByName: z.string(),
    createdAt: z.string(),
});
export const ShiftInput = z.object({
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    kind: ShiftKind.default('family'),
    assigneeId: z.string().nullable().optional(),
    label: z.string().min(1).max(200),
    notes: z.string().max(1000).nullable().optional(),
});
// Documents
export const DocumentCategory = z.enum([
    'medical',
    'scan',
    'manuscript',
    'legal',
    'photo',
    'other',
]);
export const DocumentCategoryLabels = {
    medical: 'Medical',
    scan: 'Scan / Imaging',
    manuscript: 'Manuscript',
    legal: 'Legal',
    photo: 'Photo',
    other: 'Other',
};
export const DocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    size: z.number(),
    category: DocumentCategory,
    title: z.string().nullable(),
    description: z.string().nullable(),
    uploadedById: z.string(),
    uploadedByName: z.string(),
    createdAt: z.string(),
});
export const DocumentMetaInput = z.object({
    category: DocumentCategory.default('other'),
    title: z.string().max(200).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
});
// Med reminders
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');
export const MedScheduleSchema = z.object({
    id: z.string(),
    medName: z.string(),
    medDose: z.string().nullable(),
    timeLocal: z.string(),
    timezone: z.string(),
    leadMinutes: z.number().int(),
    enabled: z.boolean(),
    lastFiredForDate: z.string().nullable(),
    createdById: z.string(),
    createdByName: z.string(),
    createdAt: z.string(),
});
export const MedScheduleInput = z.object({
    medName: z.string().min(1).max(120),
    medDose: z.string().max(120).nullable().optional(),
    timeLocal: TimeOfDay,
    timezone: z.string().min(1).max(64).default('Australia/Melbourne'),
    leadMinutes: z.number().int().min(0).max(240).default(10),
    enabled: z.boolean().default(true),
});
// Meal ideas
export const MealTime = z.enum([
    'Breakfast',
    'Morning tea',
    'Lunch',
    'Afternoon tea',
    'Dinner',
    'Snack',
]);
export const MEAL_TIMES = [
    'Breakfast', 'Morning tea', 'Lunch', 'Afternoon tea', 'Dinner', 'Snack',
];
export const MealIdeaSchema = z.object({
    id: z.string(),
    text: z.string(),
    mealTimes: z.array(MealTime),
    createdById: z.string(),
    createdByName: z.string(),
    createdAt: z.string(),
});
export const MealIdeaInput = z.object({
    text: z.string().min(1).max(200),
    mealTimes: z.array(MealTime).min(1),
});
// Web Push
export const PushSubscriptionInput = z.object({
    endpoint: z.url(),
    keys: z.object({
        p256dh: z.string().min(1),
        auth: z.string().min(1),
    }),
    userAgent: z.string().max(500).optional(),
});
// Telegram
export const TelegramLinkCodeSchema = z.object({
    code: z.string(),
    expiresAt: z.string(),
});
